import * as React from 'react';
import * as _ from 'lodash';
import { Alert, Button, ControlLabel, FormControl, FormGroup, HelpBlock, Label, Modal } from 'react-bootstrap'
import { IListItem, IXmTracking } from '@nkr/models/index';
import { XmType } from '@nkr/enums/xm-type';
import { XmResult } from '@nkr/enums/xm-result';
import FileUpload from '@nkr/components/file-upload';
import { DropEvent, FileRejection } from 'react-dropzone';
import UploadFile from '../center-admin/billing/view-edit/upload-file';

interface IProps {
    show: boolean,
    data: IXmTracking,
    resultList: IListItem[],
    onSubmit: (result: number, cellCount: number, cellViability: number, pronaseTreated: number, isDcComplete: number, rootCause: string, correctiveAction: string, initials: string, file: File) => void,
    onCancel: () => void
}

interface IValidation {
    result: boolean,
    cellCount: boolean,
    cellViability: boolean,
    pronaseTreated: boolean,
    isDcComplete: boolean,
    rootCause: boolean,
    correctiveAction: boolean,
    initials: boolean,
    file: boolean
}

interface IState {
    result: number,
    cellCount: number,
    cellViability: number,
    pronaseTreated: number,
    isDcComplete: number,
    rootCause: string,
    correctiveAction: string,
    initials: string,
    file: File,
    validation: IValidation,
    dirty: boolean
}

const initialState: IState = {
    result: -1,
    cellCount: -1,
    cellViability: -1,
    pronaseTreated: -1,
    isDcComplete: -1,
    rootCause: '',
    correctiveAction: '',
    initials: '',
    file: null,
    validation: {
        result: true,
        cellCount: true,
        cellViability: true,
        pronaseTreated: true,
        isDcComplete: true,
        rootCause: true,
        correctiveAction: true,
        initials: true,
        file: true
    },
    dirty: false
};

class RecordResults extends React.Component<IProps, IState> {
    readonly state: IState = initialState;

    componentWillReceiveProps = (nextProps: Readonly<IProps>, nextContext: any) => {
        if (nextProps.show && this.props.show == false) {
            this.setState({
                ...initialState
            });
        }
    }

    validate = (): IValidation => {
        let { result, cellCount, cellViability, pronaseTreated, isDcComplete, rootCause, correctiveAction, initials, file } = this.state;
        let cellCountValid = this.props.data.isViracorCryo ? cellCount > -1 : true;
        let cellViabilityValid = this.props.data.isViracorCryo ? cellViability > -1 : true;
        let pronaseTreatedValid = this.props.data.isViracorCryo ? pronaseTreated != -1 : true;
        let isDcCompleteValid = this.props.data.xmTypeId == XmType.ScreeningXm && result == XmResult.Acceptable ? isDcComplete != -1 : true;
        let rootCauseValid = result == XmResult.Unacceptable ? rootCause != '' : true;
        let correctiveActionValid = result == XmResult.Unacceptable ? correctiveAction != '' : true;
        let fileValid = this.props.data.xmTypeId == XmType.FinalXm ? file != null : true;

        return {
            result: result > 0,
            cellCount: cellCountValid,
            cellViability: cellViabilityValid,
            pronaseTreated: pronaseTreatedValid,
            isDcComplete: isDcCompleteValid,
            rootCause: rootCauseValid,
            correctiveAction: correctiveActionValid,
            initials: initials != '',
            file: fileValid
        };
    }

    isValid = () => {
        let { result, cellCount, cellViability, pronaseTreated, isDcComplete, rootCause, correctiveAction, initials, file } = this.state.validation;
        return result && cellCount && cellViability && pronaseTreated && isDcComplete && rootCause && correctiveAction && initials && file;
    }

    handleNumberChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let field = e.currentTarget.name;
        let value = parseInt(e.currentTarget.value);
        let state = _.cloneDeep(this.state);

        if (field == 'result') {
            state.result = value;
            state.rootCause = '';
            state.correctiveAction = '';
            state.isDcComplete = -1
        }

        if (field == 'cellCount')
            state.cellCount = value;

        if (field == 'cellViability')
            state.cellViability = value;

        if (field == 'pronaseTreated')
            state.pronaseTreated = value;

        if (field == 'isDcComplete')
            state.isDcComplete = value;

        this.setState(state, () => {
            if (this.state.dirty) {
                let validation = this.validate();
                this.setState({
                    validation
                });
            }
        });
    };

    handleTextChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let field = e.currentTarget.name;
        let value = e.currentTarget.value;
        let state = _.cloneDeep(this.state);

        if (field == 'rootCause') {
            state.rootCause = value;
        }

        if (field == 'correctiveAction') {
            state.correctiveAction = value;
        }

        if (field == 'initials') {
            state.initials = value;
        }

        this.setState(state, () => {
            if (this.state.dirty) {
                let validation = this.validate();

                this.setState({
                    validation
                });
            }
        });
    };

    handleSelectFile = (file: File) => {
        this.setState({
            file
        }, () => {
            if (this.state.dirty) {
                let validation = this.validate();

                this.setState({
                    validation
                });
            }
        });
    };

    handleRemoveFile = () => {
        this.setState({
            file: null
        }, () => {
            if (this.state.dirty) {
                let validation = this.validate();

                this.setState({
                    validation
                });
            }
        });
    };

    handleSubmit = () => {
        let validation = this.validate();

        this.setState({
            validation,
            dirty: true
        }, () => {
            if (this.isValid()) {
                let { result, cellCount, cellViability, pronaseTreated, isDcComplete, rootCause, correctiveAction, initials, file } = this.state;
                this.props.onSubmit(result, cellCount, cellViability, pronaseTreated, isDcComplete, rootCause, correctiveAction, initials, file);
            }
        });
    }

    render = () => {
        const { cellCount, cellViability, pronaseTreated, result, isDcComplete, rootCause, correctiveAction, initials, validation, dirty } = this.state;

        if (this.props.data == null)
            return null;

        const cellCountRange = _.range(0, 10000001, 100000);
        const cellViabilityRange = _.range(0, 101);

        // exclude "Antigens Confirmed"
        const resultList = this.props.resultList.filter(x => x.id != 3);

        const isValid = this.isValid();

        return (
            <Modal show={this.props.show} backdrop='static' onHide={this.props.onCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Record {this.props.data.xmTypeDescription} Results for Swap {this.props.data.chainNumber}/{this.props.data.clusterNumber}/{this.props.data.positionNumber}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.props.data.xmTypeId == XmType.ScreeningXm && (
                        <Alert>
                            This is a screening XM, do not forget to records the results of the donor record review by the Donor Clearance
                            deadline in your Swaps list.
                        </Alert>
                    )}

                    {this.props.data.isViracorCryo && (
                        <>
                            <FormGroup validationState={dirty && !validation.cellCount ? 'error' : null}>
                                <ControlLabel>How many cells were recovered?</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    name='cellCount'
                                    value={cellCount}
                                    onChange={this.handleNumberChange}
                                >
                                    <option value={-1}>- Select -</option>
                                    {cellCountRange.map(item =>
                                        <option value={item}>{item.toLocaleString()}</option>
                                    )}
                                </FormControl>
                                {dirty && !validation.cellCount && (
                                    <HelpBlock>This is required</HelpBlock>
                                )}
                            </FormGroup>

                            <FormGroup validationState={dirty && !validation.cellViability ? 'error' : null}>
                                <ControlLabel>What was the cell viability?</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    name='cellViability'
                                    value={cellViability}
                                    onChange={this.handleNumberChange}
                                >
                                    <option value={-1}>- Select -</option>
                                    {cellViabilityRange.map(item =>
                                        <option value={item}>{item}%</option>
                                    )}
                                </FormControl>
                                {dirty && !validation.cellViability && (
                                    <HelpBlock>This is required</HelpBlock>
                                )}
                            </FormGroup>

                            <FormGroup validationState={dirty && !validation.pronaseTreated ? 'error' : null}>
                                <ControlLabel>Were the cells pronase treated?</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    name='pronaseTreated'
                                    value={pronaseTreated}
                                    onChange={this.handleNumberChange}
                                >
                                    <option value={-1}>- Select -</option>
                                    <option value={0}>No</option>
                                    <option value={1}>Yes</option>
                                </FormControl>
                                {dirty && !validation.pronaseTreated && (
                                    <HelpBlock>This is required</HelpBlock>
                                )}
                            </FormGroup>
                        </>
                    )}

                    <FormGroup validationState={dirty && !validation.result ? 'error' : null}>
                        <ControlLabel>XM Result</ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='result'
                            value={result}
                            onChange={this.handleNumberChange}
                        >
                            <option value={-1}>- Select -</option>
                            {resultList.map(item =>
                                <option key={'xm-result-' + item.id} value={item.id}>{item.description}</option>
                            )}
                        </FormControl>
                        {dirty && !validation.result && (
                            <HelpBlock>This is required</HelpBlock>
                        )}
                    </FormGroup>

                    {this.props.data.xmTypeId == XmType.ScreeningXm && this.state.result == XmResult.Acceptable && (
                        <FormGroup validationState={dirty && !validation.isDcComplete ? 'error' : null}>
                            <ControlLabel>Is Donor Clearance Complete?</ControlLabel>
                            <FormControl
                                componentClass='select'
                                name='isDcComplete'
                                value={isDcComplete}
                                onChange={this.handleNumberChange}
                            >
                                <option value={-1}>- Select -</option>
                                <option value={0}>No</option>
                                <option value={1}>Yes</option>
                            </FormControl>
                            {dirty && !validation.isDcComplete && (
                                <HelpBlock>This is required</HelpBlock>
                            )}
                        </FormGroup>
                    )}

                    {this.state.result == XmResult.Unacceptable && (
                        <>
                            <FormGroup validationState={dirty && !validation.rootCause ? 'error' : null}>
                                <ControlLabel>Root Cause</ControlLabel>
                                <FormControl
                                    name='rootCause'
                                    value={rootCause}
                                    onChange={this.handleTextChange}
                                />
                                {dirty && !validation.rootCause && (
                                    <HelpBlock>This is required</HelpBlock>
                                )}
                            </FormGroup>

                            <FormGroup validationState={dirty && !validation.correctiveAction ? 'error' : null}>
                                <ControlLabel>Corrective Action</ControlLabel>
                                <FormControl
                                    name='correctiveAction'
                                    value={correctiveAction}
                                    onChange={this.handleTextChange}
                                />
                                {dirty && !validation.correctiveAction && (
                                    <HelpBlock>This is required</HelpBlock>
                                )}
                            </FormGroup>
                        </>
                    )}

                    {this.props.data.xmTypeId == XmType.FinalXm && (
                        <FormGroup validationState={dirty && !validation.file ? 'error' : null}>
                            <ControlLabel>Upload Results</ControlLabel>
                            <div>
                                <UploadFile
                                    elementName='finalXmFile'
                                    valid={validation.file}
                                    disable={false}
                                    accept='.pdf'
                                    onSelectFile={this.handleSelectFile} />

                                {this.state.file != null && (
                                    <div style={{ marginLeft: 5, paddingTop: 3 }}>Selected File: <Label style={{ fontSize: '90%' }}>{this.state.file.name}</Label></div>
                                )}
                            </div>
                            {dirty && !validation.file && (
                                <HelpBlock>This is required</HelpBlock>
                            )}
                        </FormGroup>
                    )}

                    <FormGroup validationState={dirty && !validation.initials ? 'error' : null}>
                        <ControlLabel>Initials</ControlLabel>
                        <FormControl
                            style={{ width: 'initial' }}
                            name='initials'
                            value={initials}
                            onChange={this.handleTextChange}
                        />
                        {dirty && !validation.initials && (
                            <HelpBlock>This is required</HelpBlock>
                        )}
                    </FormGroup>                   
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle='primary' disabled={!isValid} onClick={this.handleSubmit}>Submit</Button>
                    <Button onClick={this.props.onCancel}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        );
    }
};

export default RecordResults;